<template>
    <section class="customizer-3D"
      :style="{ backgroundImage: 'url(' + paletteSkinImage + ')' }"
    >
      <div
        @click="rayTraceComponent($event)"
        :class="[
          'customizer-3D-scene',
          this.themeVideo,
          { 'is-apac': this.regionIsApac }
        ]"
        id="scene"
      ></div>
      <custom-tool-tip :showToolTip="showRotateMsg" cssName="coachmark" message="dragShoeRotateMsg" :asset="`${ customsAssets }/svg/customizer/icon-rotate.svg`" :image-alt="translations.rotateCustomizeMsg"></custom-tool-tip>
      <custom-tool-tip :showToolTip="showComponentMsg" cssName="component-restrict"  message="exclusiveComponentMsg" asset=""></custom-tool-tip>
      <div class="customizer-3D-rotate"
        :style="{ 'background-image': `url(${ customsAssets }/svg/customizer/icon-rotate-to-customize.svg)` }"
      >{{ translations.rotateCustomizeMsg }}</div>
    </section>
</template>
<script>
import { screenSize } from "@/libs";
import * as THREE from 'three';
import sceneSetup from '@/mixins/Customizer/SceneSetup';
import sceneManager from '@/mixins/Customizer/SceneManager';
import model3D from '@/mixins/Customizer/Model3D';
import modelHelpers from '@/mixins/Customizer/ModelHelpers';
import swatchMaterial from '@/mixins/Customizer/SwatchMaterial';
import ugc from '@/mixins/UGC';
import geometryUpdate from '@/mixins/geometryUpdates';
import customToolTip from '@/components/ToolTip';
import { themePalette } from '@/data/constants';
import undo from "@/mixins/Customizer/Undo";
import CanvasRenderer from "@/libs/graphics/CanvasRenderer";
import { decoStitch } from '@/data/constants';
import selectSwatch from "@/mixins/Customizer/SelectSwatch";
import TrayWrapperVue from '@/components/UGC/Tray/TrayWrapper/TrayWrapper.vue';
import { subgroupFeatures } from '@/data/constants';

const TWEEN = require('@tweenjs/tween.js');

const map = (value, currentStart, currentStop, targetStart, targetStop) => {
  const amountFromStart = value - currentStart;
  const currentRange = currentStop - currentStart;
  const currentRelative = amountFromStart / currentRange;
  const targetRange = targetStop - targetStart;
  const converted = targetStart + currentRelative * targetRange;
  return converted;
};

export default {
  name: "sceneSetup",
  mixins: [sceneSetup, sceneManager, model3D, modelHelpers, swatchMaterial, ugc, geometryUpdate, undo, selectSwatch],
  components: {
    customToolTip
  },
  data() {
    return {
      scene: new THREE.Scene(),
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      sceneReadyToInit: 0,
      showRotateMsg: false,
      autoRotate: {timer: null},
      showComponentMsg: false
    }
  },
  computed: {
    isApparelEnabled() {
      return this.$store.getters['utils/isApparelEnabled'];
    },
    translations(){
      return this.$store.state.translations.data;
    },
    autoRotateEnabled() {
      return this.$store.state.utils.configuration.enable360Rotation;
    },
    autoRotateTimeout() {
      return this.$store.state.utils.configuration.enable360RotationTimeout;
    },
    highlightColor() {
      return this.$store.state.utils.configuration.highlightColor;
    },
    autoRotation() {
      return this.$store.state.utils.queryParams?.autoRotation === "true";
    },
    componentRotation() {
      return this.$store.state.utils.configuration.componentRotation && this.autoRotation;
    },
    pdm() {
      return this.$store.state.utils.pdm;
    },
    componentsWithGraphicsSupport() {
      return this.$store.getters["utils/componentsWithGraphicsSupport"];
    },
    availableSwatches() {
      return this.$store.state.utils.availableSwatches;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    defaultRecipe() {
      return this.$store.state.recipe.defaultRecipe;
    },
    s3ModelsBucket() {
      return this.$store.state.endPoints.customizer.s3ModelsBucket;
    },
    customsAssets() {
      return this.$store.state.endPoints.customizer.customsAssets;
    },
    locale() {
      return this.$store.state.utils.configuration.locale;
    },
    domain() {
      return this.$store.state.utils.configuration.domain;
    },
    externalHeaders() {
      return this.$store.state.utils.externalHeaders;
    },
    regionIsApac() {
      return this.$store.getters['utils/regionIsApac'];
    },
    themeVideo() {
      const { paletteTheme } = this.$store.state.swatches;
      const video_class = paletteTheme && paletteTheme.palette_features && paletteTheme.palette_features.video_class;
      return video_class ? themePalette.themePrefix + video_class : false;
    },
    useTheme() {
      return !!this.$store.state.swatches.paletteTheme;
    },
    isBackpack() {
      return this.$store.state.utils.pdm !== null && this.$store.state.utils.pdm.sku === 'backpack';
    },
    isMobile() {
      return this.$store.getters["utils/returnScreenSize"].includes('mobile');
    },
    isDecoStichApplied(){
      return this.$store.state.geometryUpdates.decostitchApplied;
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.$store.state.utils.configuration.enablePerformanceLogging;
    },
    paletteSkinImage() {
      const { paletteTheme } = this.$store.state.swatches;
      if(paletteTheme) {
        return `${this.customsAssets}/images/customizer/themes/${paletteTheme.palette_features.palette_background}`;
      }
      return '';
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
    activeUgcFeatureId() {
      return this.$store.getters["ugc/tempData/getActiveUGCIndexId"];
    },
    disableSizeSelector() {
      return this.$store.state.utils.configuration.ugc.disableSizeSelector;
    },
    isAnimationPaused () {
      return this.$store.state.utils.isAnimationPaused;
    }
  },
  watch: {
    pdm(newVal) {
      if (newVal !== undefined) {
        this.sceneReadyToInit++;
        this.$model3D.sceneHelpers.setPdm({ pdm: this.pdm });
      }
    },
    availableSwatches(newVal) {
      if (newVal !== undefined) {
        this.sceneReadyToInit++;
        this.$model3D.sceneHelpers.setSwatches({ swatches: this.availableSwatches});
      }
    },
    recipe_v2: {
      handler(newVal) {
        if (newVal !== undefined) {
          const recipe_v2 = JSON.parse(JSON.stringify(this.recipe_v2));

          this.$model3D.sceneHelpers.setRecipe({ recipe_v2 });
          this.sceneReadyToInit++;
        }
      },
      deep: true
    },
    defaultRecipe: {
      handler(newVal) {
        if (newVal !== undefined) this.sceneReadyToInit++;
      },
      deep: true
    },
    async sceneReadyToInit(newVal) {

      if (this.$model3D.isValidBrowser() && newVal === 5) {
        await this.createScene();
      }
    }
  },
  methods: {
    async createScene() {
      let sizeToload;
      const domain = this.$store.state.utils.configuration.enableS3Customizer ? "" : this.domain;
      this.$model3D.initDracoDecoder({domain, env: this.$store.state.utils.queryParams.env});
      this.$model3D.setProperty({ property: 'rootObj', value: new THREE.Object3D() });
      this.$model3D.setSubProperty({ property: 'rootObj', subproperty: 'name', value: 'rootObj' });
      this.setupRenderer();
      this.setupCamera();
      this.setupLights();
      this.$nextTick( async () => {
        this.loadNew3DAssets().then(({ url, size }) => {
          sizeToload = this.disableSizeSelector ? this.$store.state.utils.bucketSizes[this.pdm.ugcDefaultSize] : size;
          return this.create3DObject({ url, size });
        })
        .then(() => {
          if (this.enablePerformanceLogging)
            console.log("3d Model Ready: ", new Date().getTime() / 1000 - this.initialTime);
          this.$store.dispatch('wait/end', 'loading 3dModels', { root: true });

          this.initFloor();
          return this.loadDefaultAssets();
        })
        .then(() => {
          this.$store.dispatch('wait/end', 'loading default textures', { root: true });
          this.$store.dispatch('wait/end', 'loading stitches', { root: true });
          this.$model3D.sceneHelpers.setDefaultTextures({default_textures: this.default_textures});
          if (this.enablePerformanceLogging)
            console.log("Default texures,Stitching Ready: ", new Date().getTime() / 1000 - this.initialTime);
          this.updateCameraPosition();
          this.controls.update();
          this.resizeCanvas();
          this.animate3DModel();
          this.setupModelDefaults();
          if (this.enablePerformanceLogging)
            console.log("setup Model Defaults Ready: ", new Date().getTime() / 1000 - this.initialTime);

          return this.loadStitches(sizeToload);
        })
        .then(async () => {
          // This is to apply exclusivity logic in precreated recipe
          // since the current component swatch is not validated in the group
          // of swatches
          if (this.$store.state.utils.queryParams.recipe)
            this.getSwatchRelationBySwatchId({
              currentComponent: this.$store.state.utils.pdm.default_component,
              swatchId: this.recipe_v2.components[this.$store.state.utils.pdm.default_component]?.swatch_id
            });
          if(this.queryParams.slim) await this.getIgnoredSwatches(this.availableSwatches);
          return this.applyRecipe({recipe: this.recipe_v2, applyingRecipe: true})
        })
        .then(() => {

          if (this.enablePerformanceLogging)
            console.log("apply Recipe Ready: ", new Date().getTime() / 1000 - this.initialTime);
          
          // Compile the renderer and wait one more frame before removing the preloader.
          this.$model3D.renderer.compile(this.scene, this.camera);
          this.controls.update();
          this.$model3D.renderer.render( this.scene, this.camera );

          this.$store.commit('preloader/SET_SHOW_PRELOADER', false);
          this.$eventBus.$emit('preloader-finished-loading');
          
          if(this.$store.state.utils.configuration.enableSnapshotService) 
            this.$eventBus.$emit('render-start-over-image');

          // Open the buy panel by default
          if(this.$store.state.utils.queryParams.buypanel === '1') {
            this.$eventBus.$emit('buyButtonClicked');
          }

          // If enableUGC is false the ugc modal shouldn't be shown in spite of the url
          // has ugc_image info
          if (this.$store.getters["utils/isUGCenabled"]) {
            this.$nextTick(() => {
              this.$eventBus.$emit('preloader-finished-loading');
            });
          } else {
            this.$eventBus.$off('preloader-finished-loading');
          }

          if(this.queryParams.slim) {
            this.$nextTick(()=> {
              this.$nextTick(()=> {
                this.startScreenshotForService();
              });
            });
          } else if (this.autoRotation || this.$store.state.utils.configuration.enableAutoRotate) {
            this.rotateModel();
          } else {
            this.showRotateMsg = true;
            setTimeout(() => {
              this.showRotateMsg = false;
              this.$eventBus.$emit("component-selected", this.activeComponent);
            }
            , 3000);
          }
        })
      })
    },
    isUGCTmallRecipe() {
      const components = this.$store.getters["utils/getComponentsUgc"]({ recipe: this.recipe_v2 });
      let precreatedUGC = false;

      if (components.length > 0) {
        components.forEach(componentName => {
          const UGCasSwatch = this.$ugc.isUGCFromSwatch(this.recipe_v2.components[componentName].swatch_id);
          const swatchId = this.recipe_v2.components[componentName].swatch_id;
          precreatedUGC = this.$store.getters["swatches/isUGCFromPrecreated"]({ swatchId, componentName });
        });
      }
      if (this.$store.state.utils.configuration && this.$store.state.utils.configuration.integrations.tmall.enabled && precreatedUGC) {
        return true;
      }
    },
    create3DObject({ url, size, isTransferDesign = false, originalModel = null }) {
      const sizeToLoad = this.disableSizeSelector ? null : size;
      return this.load3Dmodel({size: sizeToLoad, originalModel})
        .then(() => {
          const { s3SwatchesBucket, customsAssets, ugcImageBucket } = this.$store.state.endPoints.customizer;
          this.$model3D.modelObj.position.set(0, 0, 0);
          this.scene.position.set(0, 0, 0);
          this.$model3D.rootObj.add(this.$model3D.modelObj);
          this.$model3D.rootObj.scale.set(1, 1, 1);
          this.$model3D.rootObj.position.set(0, 0, 0);
          this.scene.scale.set(1, 1, 1);
          this.scene.add(this.$model3D.rootObj);
          this.$model3D.sceneHelpers.parseModelComponents(this.$model3D.modelObj);
          this.$model3D.sceneHelpers.setScene({ scene: this.scene });
          this.$model3D.sceneHelpers.setRootObject({ object3d: this.$model3D.rootObj });
          this.$model3D.sceneHelpers.setModel({ model: this.$model3D.modelObj });
          this.$model3D.sceneHelpers.setHighlightColor({ color: this.highlightColor });
          this.$model3D.sceneHelpers.swatchesRoot = `${ s3SwatchesBucket }assets`;
          this.$model3D.sceneHelpers.assetsRoot = `${ customsAssets }`;
          if (isTransferDesign) {
            this.$store.commit("swatches/RESET_RELATION_BY_FEATURE");
            this.$model3D.sceneHelpers.setDefaultTextures({default_textures: this.default_textures});
            this.updateCameraPosition();
            this.controls.update();
            this.resizeCanvas();
            this.setupModelDefaults();
            this.initFloor();
            this.loadStitches()
            .then(() => this.loadDefaultAssets())
            .then(() => new Promise((resolve, reject) => { this.applyRecipe({recipe: this.recipe_v2, repositionUgc: false, isNewModel: true, resolve, reject, applyingRecipe: true}) }))
            .then(() => {
              const { default_component, default_recipe } = this.$store.state.utils.pdm;
              const component = this.recipe_v2.components[default_component];

              if (component.swatch_id !==
                this.defaultRecipe.components[default_component].swatch_id) {
                this.applyExclusivitybyDefaultComponent();
              }

              if (this.isApparelEnabled && this.componentsWithGraphicsSupport.length) {
                CanvasRenderer.destroyAll();
                this.$eventBus.$emit("graphic-scene-updated");
              }
              this.$store.commit('utils/SET_IS_DESIGN_TRANSFERING', false);
              this.$store.commit('utils/SET_IS_DESIGN_TRANSFER_APPLIED', true);
              this.$customizerDataLayer.logAnalyticsEvent({
                type: this.$customizerDataLayer.types.customizerTransferDesign,
                eventLabel: 'Newly Selected Model Loaded',
                forceEcomDataLayerFormat: true,
              });
              const startOverImage = `${ugcImageBucket}/product_images/${default_recipe}/md-checkerboard_creator-${default_recipe}.jpg`;
              this.$store.commit('modals/SET_START_OVER_PRODUCT_IMAGE', {
                recipeId: default_recipe,
                data: startOverImage
              });
            })
            .catch((err) => console.error('Transfering design - load model: ', err));
          }
        });
    },
    animate3DModel() {
      requestAnimationFrame(this.animate3DModel);
      if(this.isAnimationPaused) return;
      TWEEN.default.update();
      this.controls.update();
      this.$model3D.renderer.render( this.scene, this.camera );
    },
    verifyApplyDecoStich({swatch, recipeComponents, component}) {
      const { componentToHide } = this.pdm.components.decoStitch?.options || {};
      //add back sideStripe component in case was applied before deco stitch
      if(component === componentToHide &&  recipeComponents?.[component].subgroup !== decoStitch  && this.$store.state.geometryUpdates.decostitchApplied){
          if(swatch) {
            this.applyDecoStitch(swatch, componentToHide);
          }
      }
      if (swatch?.apply_decostitch && componentToHide &&
          recipeComponents[componentToHide]?.material_type !== decoStitch) {
          const swatch = this.$store.getters["swatches/returnSwatchbyMaterialType"]({ materialType: decoStitch });
          if(swatch) {
            this.applyDecoStitch(swatch, componentToHide);
          }
      }
    },
    async getIgnoredSwatches(swatchData) {
      const swatchIds = swatchData.map(swatchObj => Number(swatchObj.id));
      const recipeSwatchIds = Object.entries(this.$store.state.recipe.recipe_v2.components).map(component => component[1].swatch_id);
      const ignoredSwatches = recipeSwatchIds.filter(swatchId => !swatchIds.includes(swatchId));
      if(!ignoredSwatches.length) return;
      await this.$store.dispatch('utils/GET_AVAILABLE_SWATCHES_BY_ID', { swatches: recipeSwatchIds, swatchData});
    },
    async applyRecipe({recipe, repositionUgc = false, isNewModel = false, resolve = () => {}, reject = () => {}, applyingRecipe = true }) {
      const { components } = recipe;
      const promises = [];
      if (!components) {
        return;
      }
      Object.keys(components).forEach(componentName => {
        let recipeObj = components[componentName];

        const { sub_components: subComponents } = recipeObj;
        const swatchObj = this.$store.getters["swatches/returnSwatchObj"]({ swatchId: recipeObj.swatch_id });
        if(this.enablePerformanceLogging)
          console.log(`${componentName} Swatch Changed to ${recipeObj.swatch_id}: `, new Date().getTime() / 1000 - this.initialTime);

        this.verifyApplyDecoStich({swatch: swatchObj, recipeComponents:components, component:componentName});

        if (recipeObj.material_type !== "graphic" && recipeObj.ugc && !this.$store.getters["swatches/isUGCFromSwatch"]({ swatchId:  recipeObj.swatch_id })) {
         if (!repositionUgc) {
            this.applyUGCFromPrecreatedRecipe(componentName);
            if(this.activeUGCIndex !== 1) this.clearUgcStorage();
            // TODO if subgroup is library, we should use image_library to check relation swatches - feature should come from
            const featureIdQuery = (recipeObj.subgroup === 'library') ? "image_library" : "ugc";
            const featureIdBlock = subgroupFeatures[recipeObj.subgroup];

            promises.push(
              this.applyUgc3DModel(true)
                .then(() => this.$store.dispatch("swatches/GET_RELATION_SWATCH_BY_FEATURE_ID", featureIdQuery))
                .then(response => {
                  this.$store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", {swatches: response.swatches_not_allowed, feature: featureIdBlock});
                })
            );
          }
        } else if ((swatchObj?.geometry_updates && swatchObj?.geometry_updates.includes(decoStitch))){
            promises.push(this.applyDecoStitch(swatchObj));
        } else {
          if(swatchObj?.geometry_updates) {
            promises.push(
              this.updateModelGeometry({ componentName, applyingRecipe, swatchObj })
                .then(()=>{
                  const swatchGroup = this.$store.getters["swatches/returnSwatchGroupName"]({ swatch:  swatchObj })
                  if (swatchObj.upgrade_options || swatchGroup === 'upgradeoptions' || swatchObj.self_removable) {
                    this.$store.commit('geometryUpdates/SET_UPGRADE_OPTIONS', {
                      swatchId: swatchObj.id,
                      status: typeof this.$store.state.geometryUpdates.upgradeOptions[swatchObj.id] !== 'undefined' ? !this.$store.state.geometryUpdates.upgradeOptions[swatchObj.id].status : true
                    })
                  }
                  const configGroup = this.validateComponentGrouping({componentName});
                  return this.applySwatch(componentName, recipeObj.swatch_id, configGroup)
                })
            );
          } else if (swatchObj?.upgrade_options) {
            this.applyUpgradeOptions({upgradeOptions: swatchObj.upgrade_options, swatchId: swatchObj.id}).then(()=>{
              this.updateRecipeWithMerchandisingSwatchRules({swatch: swatchObj, componentName: this.activeComponent});
              this.$store.commit('utils/SET_TRAY_DISABLED_STATUS', { element: 'swatches', status: false });
            });
          } else {
            const configGroup = this.validateComponentGrouping({componentName});
            promises.push(this.applySwatch(componentName, recipeObj.swatch_id, configGroup)); // If dont exist product options push applySwatch promise
          }
        }

        if (recipeObj.personalization) {
          recipeObj.personalization.forEach((data, index) => {
            this.$store.commit('personalization/SAVE_DATA', { text: data.text, index: index, data: data });
          });
        }

        if (subComponents) {
          Object.keys(subComponents).forEach((subComponent) => {
            const subComponentData = subComponents[subComponent];
            if (subComponentData.material_type !== 'graphic' && subComponentData.ugc &&
              !this.$store.getters['swatches/isUGCFromSwatch']({ swatchId: subComponentData.swatch_id })) {
              promises.push(
                this.$store.dispatch('swatches/GET_RELATION_SWATCH_BY_FEATURE_ID', 'ugc')
                  .then((response) => {
                    this.$store.commit(
                      'swatches/SET_DISABLED_SWATCHES_BY_FEATURE',
                      { feature: 'ugc', swatches: response.swatches_not_allowed },
                    );
                  })
              );
            }
          });
        }
      });

      const isPersonalized = this.$store.getters["personalization/isPersonalizationInComponentRecipe"]();

      if(isPersonalized) {
        await this.$personalization.applyPersonalizationRecipe({ componentName: this.$store.getters["personalization/getPersonalizationComponentName"] });
        this.$store.commit('personalization/RESET_DATA');
      }

      return Promise.all(promises)
        .then(() => {
          this.applyUGCFeatureGeometry();
          if (this.$store.state.utils.configuration.enableCollapsingSwatches) {
            if(this.$store.state.utils.isRandomizeApplied) {
              const { default_component } = this.$store.state.utils.pdm;
              const defaultComponent = this.recipe_v2.components[default_component];
              const swatchObj = this.$store.getters["swatches/returnSwatchObj"]({ swatchId: defaultComponent.swatch_id});
              this.$store.commit("swatches/SET_TABS_SELECTED", {
                  component: default_component,
                  tab: this.$store.getters["swatches/returnSwatchGroupName"]({ swatch:  swatchObj })
              });
              this.$eventBus.$emit("select-component", default_component);
            }
            this.$eventBus.$emit("blockRelationSwatches");
            this.$eventBus.$emit("loadSwatches");
          }
          resolve();
        }).catch(reject);
    },
    applyUgc3DModel(loadingPrecreatedRecipe) {
      const { modelBySize, defaultSize } = this.$store.state.sceneSetup;
      const { responses, activeUGCComponent } = this.tempData;
      const size = responses.assets3d && responses.assets3d[activeUGCComponent] && responses.assets3d[activeUGCComponent].model.size;
      const tempDataSize = size && size.split(" ")[0];
      // Checks if the 3D model is a different size that what the user has selected adn therefore needs to be changed.
      if (!this.disableSizeSelector && loadingPrecreatedRecipe && defaultSize !== tempDataSize) {
        return this.finishUGCFlow(loadingPrecreatedRecipe,  this.controls)
          .catch((err) => console.error('load3Dmodel precreated: ', err));
      } else if (!this.disableSizeSelector && modelBySize !== tempDataSize) {
        return this.load3Dmodel({size})
          .then(() => {
            this.$model3D.modelObj.position.set(0, 0, 0);
            this.$model3D.setProperty({ property: 'rootObj', value: this.$model3D.sceneHelpers.getRootObject() });
            this.$model3D.rootObj.add(this.$model3D.modelObj);
            this.$model3D.sceneHelpers.getScene().add(this.$model3D.rootObj);
            this.$model3D.sceneHelpers.parseModelComponents(this.$model3D.modelObj);
            this.$model3D.sceneHelpers.setModel({ model: this.$model3D.modelObj });
            this.$store.commit("geometryUpdates/SET_DECOSTITCH_APPLIED", false);
            return this.loadStitches(size);
          })
          .then(() => this.loadDefaultAssets())
          .then(() => {
            this.$model3D.sceneHelpers.parseModelComponents(this.$model3D.modelObj);
            this.$model3D.sceneHelpers.setDefaultTextures({default_textures: this.default_textures});
            this.$eventBus.$emit("update-camera-position");
            this.initFloor();
            return this.setupModelDefaults();
          })
          .then(() => this.applyRecipe({recipe: this.recipe_v2, repositionUgc: true, isNewModel: true}))
          .then(() => this.finishUGCFlow(loadingPrecreatedRecipe, this.controls))
          .catch((err) => console.error('load3Dmodel: ', err));
      } else {
        return this.finishUGCFlow(loadingPrecreatedRecipe, this.controls);
      }
    },
    initUGCAppliance({ personalizationData }) {
      let featureIdQuery = "ugc";
      let featureIdBlock = "ugc";
      if(this.activeUGCIndex !== 1) this.clearUgcStorage();
      if(this.activeUGCIndex === 1 && this.activeUgcFeatureId === 'imageLibrary') {
        featureIdQuery = "image_library";
        featureIdBlock = "imageLibrary";
      }
      this.applyUgc3DModel()
        .then(() => {
          if (personalizationData) {
            this.$store.commit("personalization/RESET_DATA");
          }
          return this.$store.dispatch("swatches/GET_RELATION_SWATCH_BY_FEATURE_ID", featureIdQuery);
        })
        .then(response => {
          this.$store.commit(
            'swatches/SET_DISABLED_SWATCHES_BY_FEATURE',
            { swatches: response.swatches_not_allowed, feature: featureIdBlock },
          );

          if (this.tempData.applyUGCComponents?.length >= 2) {
            this.$eventBus.$emit('blockRelationSwatches');
          }

          // Update Footbed Logos by feature
          this.applyUGCFeatureGeometry();
          // Set onbeforeunload event
          this.$eventBus.$emit("set-onbeforeunload", true);
          // updates tabs
          this.$eventBus.$emit("block-features-by-swatch-id");
          this.$eventBus.$emit("selectedTab");
          this.$modal.hide(this.$store.state.utils.configuration.enableNewUgcModal ? "newUgcModal" : "ugcModal");
        })
        .catch(err => console.error('initUGCAppliance: ', err))
        .finally(() => {
          this.$eventBus.$emit("disable-finish-button");
          if (this.$store.state.ugc.tempData.activeUGCIndex === 1)
            this.$eventBus.$emit('image-library-reset-states');
        });
    },
    isIPhone(){
      return !!navigator.platform && /iPhone/.test(navigator.platform);
    },
    setScreenSize(e) {
      this.$store.commit('utils/SET_SCREEN_SIZE', screenSize.size());

      this.$nextTick(() => {
        if (window.outerHeight && window.outerWidth) {
          if (window.innerHeight > window.innerWidth) {
            // portrait
            this.windowWidth = Math.min(window.innerWidth, window.outerWidth);

            if (window.outerHeight === window.screen.height) {
              // for chrome virtual devices
              this.windowHeight = Math.min(window.innerHeight, window.outerHeight);
            }
            else {
              // for small mobile devices
              this.windowHeight = window.innerHeight;
              if (this.windowWidth < 415 && e.type == 'resize' && this.isIPhone()) {
                //iOS only adjustment
                this.windowHeight += 75;
              }
            }
          }
          else {
            // landscape
            if (window.innerWidth < window.outerWidth || window.innerHeight < window.outerHeight) {
              // fix for in window chrome inspector
              this.windowWidth = window.innerWidth;
              this.windowHeight = window.innerHeight;
            }
            else {
              this.windowWidth = Math.max(window.innerWidth, window.outerWidth);
              this.windowHeight = Math.min(window.innerHeight, window.outerHeight);
            }
          }
        }
        else {
          // no outerHeight detected
          this.windowWidth = window.innerWidth;
          this.windowHeight = window.innerHeight;
        }

        this.resizeCanvas();
      });
    },
    resizeCanvas() {
      if (this.camera) {
        const externalHeader = document.getElementById('masthead');

        let height = this.windowHeight;
        // This solves the issue with the height when the app is loaded in the iframe
        // the innerHeight is not the correct value
        let isIframeSameOrigin;
        try {
          isIframeSameOrigin = window.parent.location.href !== window.location.href;
        } catch {
          isIframeSameOrigin = false;
        }
        if (this.$store.getters['utils/canvasActive'] && isIframeSameOrigin) {
          // need to send a postMessage to get the inner height from parent.
          height = window.innerHeight;
        }
        if (externalHeader && externalHeader.clientHeight && this.regionIsApac) {
          height -= externalHeader.clientHeight;
        }

        let width = this.windowWidth;

        const isSplashPageActive = this.$store.state.utils.isSplashModalOpen;
        const isActiveEditor = this.$store.state.graphicsController.editorVisible;

        if(isSplashPageActive && !isActiveEditor) {
          if (isSplashPageActive && this.isMobile) {
            height = width;
          } else if (isSplashPageActive) {
            if (this.isBackpack) width = height = map(this.windowWidth, 1024, 1920, 280, 465);
            else width = height = map(this.windowWidth, 1024, 1920, 360, 600);
          }
        }

        this.camera.aspect = width/height;
        this.camera.updateProjectionMatrix();
        this.$model3D.renderer.setSize( width, height );
        this.responsiveModelDefaults();
        this.controls.reset();
      }
    },
    applyUGCSwatch({ component, ugc, resolve, reject }) {
      this.applyUGCAsSwatch({ component, ugc }).then(() => {
        this.reApplyDecostitch();
        resolve();
      }).catch(reject);
    },
    activateExclusiveComponentToolTip() {
      this.showComponentMsg = true;
      setTimeout(() => this.showComponentMsg = false, 5000);
    },
    /**
     *  Removed this function due to affect splash page with passcode
     */
    resizeThemeBackground() {
      if(this.useTheme) {
        const externalHeader = document.getElementById('masthead');
        let sizeDifference = 0;

        if (this.regionIsApac && externalHeader) {
          sizeDifference = externalHeader.clientHeight;
        }

        let windowHeight = this.windowHeight;

        if (this.$store.getters['utils/noEsiActive'] && this.$store.getters['utils/isIframeSameOrigin']
            && this.$store.getters["utils/returnScreenSize"].includes('mobile')) {
          // need to send the inner height from parent.
          windowHeight = window.parent.innerHeight;
        }

        document.querySelector(".customizer").style.height = `${windowHeight - sizeDifference }px`;
      }
    },
    /**
     * @function changeSceneSettings
     * Change some properties when the model is shown in the splash page
     */
    changeSceneSettings(isMainScene = true) {
      this.resizeCanvas();

      if (isMainScene) {
        this.camera.zoom = (window.innerHeight / 1000).toFixed(1);
      } else if (this.isMobile) {
        if (this.isApparelEnabled) {
          this.camera.zoom = 0.7
        } else {
          this.camera.zoom = 1.8;
        }
      } else {
        if (this.isBackpack) this.camera.zoom = 1.2;
        else if (this.isApparelEnabled) {
          this.camera.zoom = map(this.windowWidth / this.windowHeight, 0.8, 2, 0.85, 0.35)
        }
        // the values below were obtained from testing with different devices and aspect ratios
        else {
          this.camera.zoom = Math.max(Math.min(map(this.windowWidth / this.windowHeight, 0.8, 2, 1.5, 0.55), 0.65), 0.4);
        }
      }
      this.$model3D.renderer.setClearColor(0xffffff, 0);
      const floor = this.$model3D.rootObj.getObjectByName('floor');
      if (floor) {
        floor.visible = isMainScene;
      }
      this.camera.updateProjectionMatrix();
      this.controls.enableZoom = isMainScene;
      this.controls.enableRotate = true;
    }
  },
  created() {
    if(!this.$model3D.isValidBrowser()){
      this.$modal.show("noWebglModal");
    }

    window.addEventListener('resize', this.setScreenSize);
    window.addEventListener('scroll', this.setScreenSize);
    this.$eventBus.$on("rotate-stop", this.rotateStop);
    this.$eventBus.$on('init-ugc-appliance', this.initUGCAppliance);
    this.$eventBus.$on('apply-recipe', this.applyRecipe);
    this.$eventBus.$on("component-selected", this.componentHighlighting);
    this.$eventBus.$on('re-apply-decostitch', this.reApplyDecostitch);
    this.$eventBus.$on('apply-ugc-swatch', this.applyUGCSwatch);
    this.$eventBus.$on("ugc-is-removed", this.removeDecostitchWhenUGCRemoved);
    this.$eventBus.$on("show-tool-tip-exclusive-comp", this.activateExclusiveComponentToolTip);
    this.$eventBus.$on("rotate-component", this.rotateModelComponent);
    this.$eventBus.$on("change-scene-settings", this.changeSceneSettings);
    this.$eventBus.$on("create-3d-object", this.create3DObject);
    this.$eventBus.$on("resize-canvas", this.resizeCanvas);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setScreenSize);
    window.removeEventListener('scroll', this.setScreenSize);
    this.$eventBus.$off("rotate-stop", this.rotateStop);
    this.$eventBus.$off('init-ugc-appliance', this.initUGCAppliance);
    this.$eventBus.$off('apply-recipe', this.applyRecipe);
    this.$eventBus.$off("component-selected", this.componentHighlighting);
    this.$eventBus.$off('re-apply-decostitch', this.reApplyDecostitch);
    this.$eventBus.$off('apply-ugc-swatch', this.applyUGCSwatch);
    this.$eventBus.$off("ugc-is-removed", this.removeDecostitchWhenUGCRemoved);
    this.$eventBus.$off("show-tool-tip-exclusive-comp", this.activateExclusiveComponentToolTip);
    this.$eventBus.$off("rotate-component", this.rotateModelComponent);
    this.$eventBus.$off("change-scene-settings", this.changeSceneSettings);
    this.$eventBus.$off("create-3d-object", this.create3DObject);
    this.$eventBus.$off("resize-canvas", this.resizeCanvas);
  }
};
</script>
